import {
    defineComponent,
    type PropType,
    type Ref,
} from "vue";

export type PublicServerDirectoryFilterState = {
    search     : Ref<string>;
    membersMin : Ref<string>;
    membersMax : Ref<string>;
    onlineMin  : Ref<string>;
    onlineMax  : Ref<string>;
    voiceMin   : Ref<string>;
    voiceMax   : Ref<string>;
    sort       : Ref<string>;
};

type PublicServerDirectorySearchProps = {
    search    : Ref<string>;
    translate : (key: string) => string;
    apply     : () => void;
};

function bindInput(target: Ref<string>) {
    return (event: Event): void => {
        target.value = (event.target as HTMLInputElement | HTMLSelectElement).value;
    };
}

export const PublicServerDirectorySearch = defineComponent({
    props: {
        search: {
            type     : Object as PropType<Ref<string>>,
            required : true,
        },
        translate: {
            type     : Function as PropType<PublicServerDirectorySearchProps["translate"]>,
            required : true,
        },
        apply: {
            type     : Function as PropType<PublicServerDirectorySearchProps["apply"]>,
            required : true,
        },
    },

    setup(props) {
        return () => (
            <form class="server-directory-search-form" onSubmit={(event) => {
                event.preventDefault();
                props.apply();
            }}>
                <label class="server-directory-search">
                    <span>{props.translate("servers.filters.search")}</span>
                    <div>
                        <i class="material-symbols-outlined">search</i>
                        <input
                            value={props.search.value}
                            onInput={bindInput(props.search)}
                            type="search"
                            placeholder={props.translate("servers.filters.search_placeholder")}
                        />
                    </div>
                </label>

                <button type="submit" class="server-directory-primary-button">
                    <i class="material-symbols-outlined">search</i>
                    {props.translate("servers.filters.search_action")}
                </button>
            </form>
        );
    },
});

export default defineComponent({
    props: {
        filters: {
            type     : Object as PropType<PublicServerDirectoryFilterState>,
            required : true,
        },
        membersMaximum: {
            type     : Number,
            required : true,
        },
        translate: {
            type     : Function as PropType<(key: string) => string>,
            required : true,
        },
        apply: {
            type     : Function as PropType<() => void>,
            required : true,
        },
        reset: {
            type     : Function as PropType<() => void>,
            required : true,
        },
    },

    setup(props) {
        return () => (
            <form class="server-directory-filters" onSubmit={(event) => {
                event.preventDefault();
                props.apply();
            }}>
                <div class="server-directory-filter-grid">
                    <fieldset>
                        <legend>{props.translate("servers.filters.members")}</legend>
                        <div class="server-directory-range-inputs">
                            <label>
                                <span>{props.translate("servers.filters.min")}</span>
                                <input
                                    value={props.filters.membersMin.value}
                                    onInput={bindInput(props.filters.membersMin)}
                                    type="number"
                                    min="100"
                                />
                            </label>
                            <label>
                                <span>{props.translate("servers.filters.max")}</span>
                                <input
                                    value={props.filters.membersMax.value}
                                    onInput={bindInput(props.filters.membersMax)}
                                    type="number"
                                    min="100"
                                />
                            </label>
                        </div>
                        <input
                            value={props.filters.membersMin.value}
                            onInput={bindInput(props.filters.membersMin)}
                            type="range"
                            min="100"
                            max={Math.max(100, Math.ceil(props.membersMaximum / 100) * 100)}
                            step="100"
                        />
                        <input
                            value={props.filters.membersMax.value}
                            onInput={bindInput(props.filters.membersMax)}
                            type="range"
                            min="100"
                            max={Math.max(100, Math.ceil(props.membersMaximum / 100) * 100)}
                            step="100"
                        />
                    </fieldset>

                    <fieldset>
                        <legend>{props.translate("servers.filters.online")}</legend>
                        <div class="server-directory-range-inputs">
                            <label>
                                <span>{props.translate("servers.filters.min")}</span>
                                <input
                                    value={props.filters.onlineMin.value}
                                    onInput={bindInput(props.filters.onlineMin)}
                                    type="number"
                                    min="0"
                                />
                            </label>
                            <label>
                                <span>{props.translate("servers.filters.max")}</span>
                                <input
                                    value={props.filters.onlineMax.value}
                                    onInput={bindInput(props.filters.onlineMax)}
                                    type="number"
                                    min="0"
                                />
                            </label>
                        </div>
                    </fieldset>

                    <fieldset>
                        <legend>{props.translate("servers.filters.voice")}</legend>
                        <div class="server-directory-range-inputs">
                            <label>
                                <span>{props.translate("servers.filters.min")}</span>
                                <input
                                    value={props.filters.voiceMin.value}
                                    onInput={bindInput(props.filters.voiceMin)}
                                    type="number"
                                    min="0"
                                />
                            </label>
                            <label>
                                <span>{props.translate("servers.filters.max")}</span>
                                <input
                                    value={props.filters.voiceMax.value}
                                    onInput={bindInput(props.filters.voiceMax)}
                                    type="number"
                                    min="0"
                                />
                            </label>
                        </div>
                    </fieldset>

                    <label class="server-directory-sort">
                        <span>{props.translate("servers.filters.sort")}</span>
                        <select
                            value={props.filters.sort.value}
                            onChange={bindInput(props.filters.sort)}
                        >
                            <option value="members">{props.translate("servers.sort.members")}</option>
                            <option value="online">{props.translate("servers.sort.online")}</option>
                            <option value="voice">{props.translate("servers.sort.voice")}</option>
                            <option value="name">{props.translate("servers.sort.name")}</option>
                        </select>
                    </label>
                </div>

                <div class="server-directory-filter-actions">
                    <button type="button" class="server-directory-secondary-button" onClick={props.reset}>
                        {props.translate("servers.filters.reset")}
                    </button>
                    <button type="submit" class="server-directory-primary-button">
                        {props.translate("servers.filters.apply")}
                    </button>
                </div>
            </form>
        );
    },
});
